import {
  formatAiInferenceResponseForDisplay,
  getAiInferencePresignedUrl,
  runAiInference,
} from '../../api/aiInference';
import { resolveRoomObjectImageSrcForDisplay } from '../roomObjectImage';
import { addTask } from '../../services/taskResolver';

export function extractTaskResultS3Key(data) {
  const taskResult = data?.taskResult ?? data?.result;
  if (taskResult == null) return null;
  if (typeof taskResult === 'string' && taskResult.trim()) return taskResult.trim();
  const key = taskResult?.resultS3Key ?? taskResult?.result_s3_key;
  return typeof key === 'string' && key.trim() ? key.trim() : null;
}

async function toChatSafeImageUrl(displayUrl) {
  if (!displayUrl) return '';
  try {
    const res = await fetch(displayUrl);
    if (!res.ok) return displayUrl;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return displayUrl;
  }
}

/**
 * POST /ai-inference with task_name photorealistic_image, then wait for task completion.
 * @param {number} roomImageId
 * @param {string} promptText
 * @returns {Promise<
 *   | { type: 'image_result', status?: string, resultS3Key: string, imageUrl: string }
 *   | { type: 'text', text: string }
 * >}
 */
export async function submitPhotorealisticImagePrompt(roomImageId, promptText) {
  const trimmed = typeof promptText === 'string' ? promptText.trim() : '';
  const res = await runAiInference({
    task_name: 'photorealistic_image',
    room_image_id: Number(roomImageId),
    prompt: trimmed || null,
  });

  const taskId = res?.taskId ?? res?.aiTaskId ?? res?.id;
  const data =
    taskId == null
      ? res
      : await new Promise((resolve, reject) => {
          addTask(taskId, {
            roomImageId,
            onResolve: resolve,
            onError: reject,
          });
        });

  const resultS3Key = extractTaskResultS3Key(data);
  if (resultS3Key) {
    const presigned = await getAiInferencePresignedUrl(resultS3Key);
    const rawUrl = presigned?.url ?? presigned?.presignedUrl;
    if (rawUrl) {
      const imageUrl = resolveRoomObjectImageSrcForDisplay(String(rawUrl));
      const chatImageUrl = await toChatSafeImageUrl(imageUrl);
      return {
        type: 'image_result',
        status: data?.status ?? data?.taskStatus ?? data?.state,
        resultS3Key,
        imageUrl,
        chatImageUrl,
      };
    }
  }

  return {
    type: 'text',
    text: formatAiInferenceResponseForDisplay(data),
  };
}
